import React, { useState } from "react";
import {
  FaUserGraduate,
  FaHome,
  FaGraduationCap,
  FaFileAlt,
  FaClipboardList,
} from "react-icons/fa";
import {
  DashboardContainer,
  SpaceBackground,
  WelcomeSection,
  WelcomeTitle,
  WelcomeText,
  DashboardGrid,
  GridColumn,
  DashboardCard,
  NotificationBar,
  ViewAllButton,
  SectionDivider,
  SuggestedCompaniesSection,
  CompanyCard,
  CompanyLogo,
  CompanyInfo,
  CompanyName,
  CompanyDescription,
  CompanyTags,
  CompanyTag,
  ApplyButton,
} from "../../../styles/components/ProStudentDashboard/styles";
import styled, { keyframes } from "styled-components";
import { Button, PageTitle } from "../../../styles/components";
import { PageHeader } from "../../../styles/components";

// Import the components
import StudentProfile from "../../../components/proStudent/StudentProfile";
import MajorsList from "../../../components/proStudent/MajorsList/MajorsList";
import Internships from "../../../pages/Internships/Internships";
import ReportManagement from "../../../components/student/ReportManagement/ReportManagement";
import ApplicationsList from "../../../components/student/ApplicationsList/ApplicationsList";
import InternshipGuidelinesVideo from "../../../components/student/InternshipGuidelines/InternshipGuidelinesVideo";

// Import mock data
import companiesData from "../../../data/mock/companies";

// Advanced moving neon glow animations
const neonPulse = keyframes`
  0% { opacity: 0.3; }
  50% { opacity: 0.8; }
  100% { opacity: 0.3; }
`;

// Animation for the moving light border
const borderGlow = keyframes`
  0% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
  50% { box-shadow: 0 0 15px rgba(99, 102, 241, 0.5), inset 0 0 8px rgba(99, 102, 241, 0.2); }
  100% { box-shadow: 0 0 10px rgba(99, 102, 241, 0.3), inset 0 0 5px rgba(99, 102, 241, 0.1); }
`;

// Orbital neon movement for the glow effect
const orbitGlow = keyframes`
  0% { transform: translateX(-100%) translateY(-100%) scale(0.8); }
  25% { transform: translateX(200%) translateY(-50%) scale(1); }
  50% { transform: translateX(200%) translateY(200%) scale(1.2); }
  75% { transform: translateX(-50%) translateY(200%) scale(1); }
  100% { transform: translateX(-100%) translateY(-100%) scale(0.8); }
`;

// Subtle perimeter scan effect
const scanEffect = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 200% 0%; }
`;

// Navigation Header Components
const NavContainer = styled.nav`
  display: flex;
  gap: 1.25rem;
  margin-bottom: 2.5rem;
  flex-wrap: wrap;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 0.85rem 1rem;
  position: relative;
  overflow: hidden;
  z-index: 1;

  /* Futuristic glassy border effect */
  border: 1px solid rgba(148, 163, 184, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  animation: ${borderGlow} 4s infinite ease-in-out;

  /* Space background gradients */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        circle at 15% 50%,
        rgba(99, 102, 241, 0.12) 0%,
        transparent 45%
      ),
      radial-gradient(
        circle at 85% 30%,
        rgba(124, 58, 237, 0.08) 0%,
        transparent 45%
      );
    z-index: -1;
  }

  /* Dynamic neon glow orb that moves around container edges */
  &:after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: -1;

    /* Star-like dots in background */
    background-image: radial-gradient(
        1px 1px at 20% 30%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 40% 70%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 60% 40%,
        rgba(255, 255, 255, 0.8) 1px,
        transparent 0
      ),
      radial-gradient(
        1px 1px at 80% 60%,
        rgba(255, 255, 255, 0.7) 1px,
        transparent 0
      );
    opacity: 0.4;
  }

  /* Moving perimeter glow effect */
  &:before {
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(99, 102, 241, 0.1) 25%,
      rgba(124, 58, 237, 0.2) 50%,
      rgba(99, 102, 241, 0.1) 75%,
      transparent 100%
    );
    background-size: 200% 100%;
    animation: ${scanEffect} 8s infinite linear;
  }

  /* Orbital neon glow */
  .neon-orb {
    position: absolute;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    background: radial-gradient(
      circle at center,
      rgba(99, 102, 241, 0.4) 0%,
      rgba(124, 58, 237, 0.2) 40%,
      transparent 70%
    );
    filter: blur(20px);
    animation: ${orbitGlow} 15s infinite ease-in-out;
    z-index: -1;
    opacity: 0.6;
  }

  /* Pulsing neon spots */
  .neon-spot-1,
  .neon-spot-2,
  .neon-spot-3 {
    position: absolute;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    z-index: -1;
    filter: blur(10px);
  }

  .neon-spot-1 {
    background: rgba(99, 102, 241, 0.7);
    top: 10px;
    right: 30%;
    animation: ${neonPulse} 3s infinite ease-in-out;
  }

  .neon-spot-2 {
    background: rgba(124, 58, 237, 0.7);
    bottom: 15px;
    left: 25%;
    animation: ${neonPulse} 4s 0.5s infinite ease-in-out;
  }

  .neon-spot-3 {
    background: rgba(79, 70, 229, 0.7);
    top: 50%;
    left: 15px;
    animation: ${neonPulse} 5s 1s infinite ease-in-out;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 1rem;
    gap: 0.85rem;
  }
`;

const NavLogo = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-shadow: 0 0 10px rgba(99, 102, 241, 0.5);

  svg {
    color: rgba(99, 102, 241, 0.9);
    filter: drop-shadow(0 0 5px rgba(99, 102, 241, 0.7));
    font-size: 1.8rem;
  }

  &:hover {
    color: white;
    text-shadow: 0 0 15px rgba(99, 102, 241, 0.7);

    svg {
      filter: drop-shadow(0 0 8px rgba(99, 102, 241, 0.9));
    }
  }
`;

const NavButton = styled(Button)`
  flex: 1;
  min-width: 150px;
  background: ${(props) =>
    props.active ? "rgba(79, 70, 229, 0.75)" : "rgba(15, 23, 42, 0.4)"};
  color: ${(props) => (props.active ? "white" : "rgba(255, 255, 255, 0.8)")};
  border: ${(props) =>
    props.active
      ? "1px solid rgba(99, 102, 241, 0.5)"
      : "1px solid rgba(148, 163, 184, 0.2)"};
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

  /* Improved hover effect with neon glow */
  &:hover {
    background: ${(props) =>
      props.active ? "rgba(79, 70, 229, 0.85)" : "rgba(99, 102, 241, 0.25)"};
    transform: translateY(-3px);
    box-shadow: ${(props) =>
      props.active
        ? "0 8px 25px -5px rgba(79, 70, 229, 0.5), 0 0 15px rgba(99, 102, 241, 0.6)"
        : "0 7px 20px -5px rgba(31, 38, 135, 0.3), 0 0 12px rgba(99, 102, 241, 0.4)"};
    color: white;
    text-shadow: 0 0 8px rgba(255, 255, 255, 0.8);

    svg {
      transform: scale(1.1);
      filter: drop-shadow(0 0 5px rgba(99, 102, 241, 0.8));
    }
  }

  /* Active state enhanced neon glow effect */
  ${(props) =>
    props.active &&
    `
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
    box-shadow: 0 0 18px rgba(99, 102, 241, 0.6), inset 0 0 8px rgba(99, 102, 241, 0.4);
  `}

  /* Futuristic gradient overlay */
  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      ${(props) =>
        props.active
          ? "rgba(129, 140, 248, 0.4) 0%, rgba(79, 70, 229, 0.4) 100%"
          : "rgba(255, 255, 255, 0.05) 0%, rgba(15, 23, 42, 0.05) 100%"}
    );
    z-index: -1;
  }

  /* Enhanced shiny animation on hover */
  &:after {
    content: "";
    position: absolute;
    width: 150%;
    height: 200%;
    top: -200%;
    left: -50%;
    background: linear-gradient(
      45deg,
      transparent 0%,
      rgba(255, 255, 255, 0.1) 45%,
      rgba(255, 255, 255, 0.4) 50%,
      rgba(255, 255, 255, 0.1) 55%,
      transparent 100%
    );
    transform: rotate(25deg);
    transition: all 0.5s cubic-bezier(0.19, 1, 0.22, 1);
    z-index: 1;
  }

  &:hover:after {
    top: 200%;
    left: 100%;
  }

  svg {
    font-size: 1.2rem;
    margin-right: 0.5rem;
    vertical-align: middle;
    position: relative;
    top: -1px;
    transition: all 0.3s ease;

    /* Enhanced icon glow effect on active state */
    ${(props) =>
      props.active &&
      `
      filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.7));
    `}
  }
`;

// Student-specific components
const ApplicationStatusCard = styled(DashboardCard)`
  h3 {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const ApplicationItem = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 1rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const ApplicationLogo = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 1rem;
`;

const ApplicationInfo = styled.div`
  flex: 1;
`;

const ApplicationTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
`;

const ApplicationCompany = styled.p`
  margin: 0 0 0.5rem 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
`;

const ApplicationStatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "rgba(234, 179, 8, 0.2)";
      case "accepted":
        return "rgba(34, 197, 94, 0.2)";
      case "rejected":
        return "rgba(239, 68, 68, 0.2)";
      default:
        return "rgba(148, 163, 184, 0.2)";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return "rgba(234, 179, 8, 0.9)";
      case "accepted":
        return "rgba(34, 197, 94, 0.9)";
      case "rejected":
        return "rgba(239, 68, 68, 0.9)";
      default:
        return "rgba(148, 163, 184, 0.9)";
    }
  }};
`;

const ApplicationDate = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
`;

const ApplicationNextStep = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.6);

  p {
    margin-bottom: 1rem;
  }
`;

const LinkButton = styled(Button)`
  display: inline-block;
  margin-top: 0.5rem;
`;

const ProfileCompletion = styled.div`
  text-align: center;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  margin-bottom: 0.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => props.width || "0%"};
  background: linear-gradient(
    90deg,
    rgba(99, 102, 241, 0.8),
    rgba(124, 58, 237, 0.8)
  );
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ProgressText = styled.p`
  margin: 0 0 1rem 0;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
`;

const ProfileTasks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const ProfileTask = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const TaskIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) =>
    props.completed ? "rgba(34, 197, 94, 0.2)" : "rgba(255, 255, 255, 0.1)"};
  color: ${(props) =>
    props.completed ? "rgba(34, 197, 94, 0.9)" : "rgba(255, 255, 255, 0.5)"};
  font-size: 0.9rem;
`;

const TaskText = styled.span`
  color: ${(props) =>
    props.completed ? "rgba(255, 255, 255, 0.9)" : "rgba(255, 255, 255, 0.6)"};
  font-size: 0.9rem;
`;

const CompleteProfileButton = styled(Button)`
  margin-top: 0.5rem;
`;

const StudentDashboard = () => {
  const [activeView, setActiveView] = useState("dashboard");
  const [notification] = useState(null);

  // Mock data for application status
  const applicationStatus = [
    {
      id: 1,
      position: "Software Development Intern",
      company: "Tech Solutions Inc.",
      status: "Pending",
      lastUpdate: "2024-03-05",
      nextStep: "Interview scheduled for March 12",
      logo: "https://via.placeholder.com/50",
    },
    {
      id: 2,
      position: "Marketing Assistant",
      company: "Global Marketing",
      status: "Accepted",
      lastUpdate: "2024-03-01",
      nextStep: "Start date: June 1, 2024",
      logo: "https://via.placeholder.com/50",
    },
    {
      id: 3,
      position: "Data Analysis Intern",
      company: "Data Insights Co.",
      status: "Rejected",
      lastUpdate: "2024-02-28",
      nextStep: "Consider applying to other positions",
      logo: "https://via.placeholder.com/50",
    },
  ];
  // Use the imported companies data instead of hardcoded data
  const suggestedCompanies = companiesData.slice(0, 1); // Get only first company for the dashboard

  const renderView = () => {
    switch (activeView) {
      case "dashboard":
        return (
          <>
            <WelcomeSection>
              <WelcomeTitle>Welcome, Student!</WelcomeTitle>
              <WelcomeText>
                Your personalized dashboard is here to help you navigate your
                academic and career journey. Explore workshops, and discover
                internship opportunities.
              </WelcomeText>
            </WelcomeSection>{" "}
            <DashboardGrid>
              <GridColumn>
                <DashboardCard>
                  <h3>Suggested Companies</h3>
                  <SuggestedCompaniesSection>
                    {suggestedCompanies.map((company) => (
                      <CompanyCard key={company.id}>
                        <CompanyLogo src={company.logo} alt={company.name} />
                        <CompanyInfo>
                          <CompanyName>{company.name}</CompanyName>
                          <CompanyDescription>
                            {company.description}
                          </CompanyDescription>{" "}
                          <CompanyTags>
                            {company.interests.map((interest) => (
                              <CompanyTag key={interest}>{interest}</CompanyTag>
                            ))}
                          </CompanyTags>
                          <ApplyButton
                            onClick={() => setActiveView("internships")}
                          >
                            Apply Now
                          </ApplyButton>
                        </CompanyInfo>
                      </CompanyCard>
                    ))}
                  </SuggestedCompaniesSection>
                </DashboardCard>
              </GridColumn>

              <GridColumn>
                <DashboardCard>
                  <h3>Profile Completion</h3>
                  <ProfileCompletion>
                    <ProgressBar>
                      <ProgressFill width="75%" />
                    </ProgressBar>
                    <ProgressText>75% Complete</ProgressText>
                    <ProfileTasks>
                      <ProfileTask completed>
                        <TaskIcon completed>✓</TaskIcon>
                        <TaskText completed>Basic Information</TaskText>
                      </ProfileTask>
                      <ProfileTask completed>
                        <TaskIcon completed>✓</TaskIcon>
                        <TaskText completed>Education Details</TaskText>
                      </ProfileTask>
                      <ProfileTask completed>
                        <TaskIcon completed>✓</TaskIcon>
                        <TaskText completed>Skills & Interests</TaskText>
                      </ProfileTask>
                      <ProfileTask>
                        <TaskIcon>○</TaskIcon>{" "}
                        <TaskText>Upload Resume</TaskText>
                      </ProfileTask>
                    </ProfileTasks>
                    <CompleteProfileButton
                      onClick={() => setActiveView("profile")}
                    >
                      Complete Profile
                    </CompleteProfileButton>
                  </ProfileCompletion>
                </DashboardCard>
              </GridColumn>
            </DashboardGrid>
            <SectionDivider />
            <DashboardGrid>
              <GridColumn style={{ gridColumn: "span 2" }}>
                <DashboardCard>
                  <h3>Internship Guidelines</h3>
                  <InternshipGuidelinesVideo
                    defaultMajor="Computer Science"
                    studentType="regular"
                  />
                </DashboardCard>
              </GridColumn>
            </DashboardGrid>
          </>
        );
      case "profile":
        return <StudentProfile isProStudent={false} />;
      case "majors":
        return <MajorsList />;
      case "internships":
        return <Internships />;
      case "reports":
        return <ReportManagement />;
      case "applications":
        return <ApplicationsList />;
      default:
        return null;
    }
  };

  return (
    <DashboardContainer>
      <SpaceBackground />
      <PageHeader>
        <PageTitle>
          <NavLogo>
            <FaUserGraduate />
            Student Dashboard
          </NavLogo>
        </PageTitle>
      </PageHeader>

      <NavContainer>
        {/* Dynamic neon glow effects */}
        <div className="neon-orb"></div>
        <div className="neon-spot-1"></div>
        <div className="neon-spot-2"></div>
        <div className="neon-spot-3"></div>

        <NavButton
          active={activeView === "dashboard"}
          onClick={() => setActiveView("dashboard")}
        >
          <FaHome /> Dashboard
        </NavButton>
        <NavButton
          active={activeView === "profile"}
          onClick={() => setActiveView("profile")}
        >
          <FaUserGraduate /> Profile
        </NavButton>
        <NavButton
          active={activeView === "majors"}
          onClick={() => setActiveView("majors")}
        >
          <FaGraduationCap /> Majors
        </NavButton>
        <NavButton
          active={activeView === "applications"}
          onClick={() => setActiveView("applications")}
        >
          <FaClipboardList /> Applications
        </NavButton>
        <NavButton
          active={activeView === "reports"}
          onClick={() => setActiveView("reports")}
        >
          <FaFileAlt /> Reports & Evaluations
        </NavButton>
      </NavContainer>

      {notification && (
        <NotificationBar type={notification.type}>
          {notification.message}
        </NotificationBar>
      )}
      {renderView()}
    </DashboardContainer>
  );
};

export default StudentDashboard;
